/**
 * Profile Service - Obsługa profili użytkowników w Supabase
 */

import { supabase } from './supabase'

export interface UserProfile {
  id: string
  user_id: string
  full_name: string | null
  nickname: string | null
  username_slug: string
  avatar_url: string | null
  bio: string | null
  total_points: number
  level: number
  role: string
  is_admin: boolean
  created_at: string
  updated_at: string
}

export interface ProfileUpdateData {
  full_name?: string
  nickname?: string
  bio?: string
  avatar_url?: string
}

/**
 * Pobierz profil użytkownika
 */
export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', userId)
      .single()

    if (error) {
      console.error('Error fetching user profile:', error)
      return null
    }

    return data
  } catch (err) {
    console.error('Error in getUserProfile:', err)
    return null
  }
}

/**
 * Utwórz profil użytkownika (jeśli nie istnieje)
 */
export async function createUserProfile(userId: string, fullName?: string): Promise<UserProfile | null> {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .insert({
        user_id: userId,
        full_name: fullName || null,
        total_points: 0,
        level: 1,
        role: 'user',
        is_admin: false
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating user profile:', error)
      return null
    }

    return data
  } catch (err) {
    console.error('Error in createUserProfile:', err)
    return null
  }
}

/**
 * Zaktualizuj profil użytkownika
 */
export async function updateUserProfile(
  userId: string, 
  updates: ProfileUpdateData
): Promise<UserProfile | null> {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId)
      .select()
      .single()

    if (error) {
      console.error('Error updating user profile:', error)
      return null
    }

    return data
  } catch (err) {
    console.error('Error in updateUserProfile:', err)
    return null
  }
}

/**
 * Pobierz lub utwórz profil użytkownika
 */
export async function getOrCreateUserProfile(
  userId: string, 
  fullName?: string
): Promise<UserProfile | null> {
  // Najpierw sprawdź czy profil istnieje
  let profile = await getUserProfile(userId)
  
  // Jeśli nie istnieje, utwórz go
  if (!profile) {
    profile = await createUserProfile(userId, fullName)
  }
  
  return profile
}

/**
 * Generuj unikalny slug dla użytkownika
 * Format: imie-ksywka-nazwisko (ksywka opcjonalna)
 * Automatyczna iteracja dla duplikatów: jan-kowalski, jan-kowalski-2, etc.
 */
export async function generateUniqueSlug(
  fullName: string, 
  nickname?: string | null,
  currentUserId?: string
): Promise<string> {
  try {
    // Wywołaj funkcję SQL w bazie danych
    const { data, error } = await supabase
      .rpc('generate_username_slug', {
        p_full_name: fullName,
        p_nickname: nickname || null,
        p_user_id: currentUserId || null
      })

    if (error) {
      console.error('Error generating slug:', error)
      // Fallback: prosty slug z timestamp
      const simple = fullName.toLowerCase().replace(/[^a-z0-9]/g, '-')
      return `${simple}-${Date.now()}`
    }

    return data as string
  } catch (err) {
    console.error('Error in generateUniqueSlug:', err)
    // Fallback
    const simple = fullName.toLowerCase().replace(/[^a-z0-9]/g, '-')
    return `${simple}-${Date.now()}`
  }
}

/**
 * Pobierz profil użytkownika po slug
 */
export async function getUserProfileBySlug(slug: string): Promise<UserProfile | null> {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('username_slug', slug)
      .single()

    if (error) {
      console.error('Error fetching user profile by slug:', error)
      return null
    }

    return data
  } catch (err) {
    console.error('Error in getUserProfileBySlug:', err)
    return null
  }
}

/**
 * Pobierz statystyki użytkownika
 */
export async function getUserStats(userId: string) {
  try {
    // Pobierz ukończone moduły
    const { data: completedModules, error: modulesError } = await supabase
      .from('user_progress')
      .select('*', { count: 'exact' })
      .eq('user_id', userId)
      .eq('completed', true)

    // Pobierz posty na forum
    const { data: forumPosts, error: postsError } = await supabase
      .from('community_posts')
      .select('*', { count: 'exact' })
      .eq('user_id', userId)

    // Pobierz zapisane artykuły (bookmarks)
    const { data: bookmarks, error: bookmarksError } = await supabase
      .from('thread_bookmarks')
      .select('*', { count: 'exact' })
      .eq('user_id', userId)

    if (modulesError || postsError || bookmarksError) {
      console.error('Error fetching user stats:', { modulesError, postsError, bookmarksError })
    }

    return {
      completedModules: completedModules?.length || 0,
      forumPosts: forumPosts?.length || 0,
      bookmarks: bookmarks?.length || 0
    }
  } catch (err) {
    console.error('Error in getUserStats:', err)
    return {
      completedModules: 0,
      forumPosts: 0,
      bookmarks: 0
    }
  }
}

